/**
 * Cliente de autenticación embebida (mejora "Login en LicitAI").
 *
 * Antes el SPA usaba `keycloak-js` y redirigía al login de Keycloak.
 * Ahora hablamos con `/api/v1/auth/*` del backend, que reenvía las
 * credenciales a Keycloak vía Direct Access Grants. El usuario no sale
 * de LicitAI en ningún momento.
 *
 * Este módulo expone:
 *   - `login(username, password)` → guarda tokens y devuelve user info
 *   - `logout()` → revoca en Keycloak y borra el storage
 *   - `refreshIfNeeded()` → renueva el access_token cuando queda poco
 *   - `getAccessToken()` → para el cliente HTTP
 *   - `getCurrentUser()` → datos derivados del JWT
 *   - `restoreSession()` → al recargar la página
 */

import { jwtDecode } from "jwt-decode";
import {
  clearTokens,
  readTokens,
  storeTokens,
  type StoredTokens,
} from "./storage";

interface KeycloakJWT {
  sub: string;
  email?: string;
  name?: string;
  preferred_username?: string;
  exp: number; // epoch seconds
  realm_access?: { roles?: string[] };
}

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  roles: string[];
}

interface TokenResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number; // seconds
  refresh_expires_in: number;
  token_type: string;
}

// En dev local el frontend corre en :3000 y el backend en :8000. Sin la
// base absoluta, el fetch golpea Vite y devuelve 404. En producción
// (mismo dominio detrás del Gateway) VITE_API_URL puede quedar vacío y
// se usa el path relativo.
const API_BASE = `${import.meta.env.VITE_API_URL || ""}/api/v1/auth`;

// Margen para renovar el token antes de que caduque (en segundos). Si
// quedan menos de N segundos, refrescamos. 60s es suficiente para evitar
// que una request en vuelo caduque a mitad de camino.
const REFRESH_THRESHOLD_SEC = 60;

let inFlightRefresh: Promise<string | null> | null = null;

export class AuthError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

async function parseError(resp: Response): Promise<string> {
  try {
    const body = await resp.json();
    if (typeof body.detail === "string") return body.detail;
  } catch {
    /* ignore */
  }
  return "Error de autenticación";
}

function tokensFromResponse(data: TokenResponse): StoredTokens {
  return {
    accessToken: data.access_token,
    refreshToken: data.refresh_token,
    expiresAt: Date.now() + data.expires_in * 1000,
  };
}

function userFromAccessToken(token: string): AuthUser {
  const decoded = jwtDecode<KeycloakJWT>(token);
  return {
    id: decoded.sub,
    email: decoded.email ?? "",
    name: decoded.name ?? decoded.preferred_username ?? "",
    roles: decoded.realm_access?.roles ?? [],
  };
}

export async function login(
  username: string,
  password: string
): Promise<AuthUser> {
  const resp = await fetch(`${API_BASE}/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password }),
  });
  if (!resp.ok) {
    throw new AuthError(resp.status, await parseError(resp));
  }
  const data: TokenResponse = await resp.json();
  storeTokens(tokensFromResponse(data));
  return userFromAccessToken(data.access_token);
}

export async function logout(): Promise<void> {
  const tokens = readTokens();
  clearTokens();
  if (!tokens) return;
  // Fire-and-forget. Si Keycloak no responde, da igual: localmente ya
  // borramos las credenciales. El endpoint del backend siempre devuelve
  // 204 aunque Keycloak falle.
  try {
    await fetch(`${API_BASE}/logout`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: tokens.refreshToken }),
    });
  } catch {
    /* ignore network errors during logout */
  }
}

/**
 * Si el access_token está cerca de caducar, lo renueva con el
 * refresh_token. Devuelve el access_token vigente o null si ya no
 * podemos renovar (refresh caducado, etc.).
 *
 * Coalescing: si dos componentes piden refresh a la vez, solo lanzamos
 * una llamada y ambos esperan al mismo resultado.
 */
export async function refreshIfNeeded(): Promise<string | null> {
  const tokens = readTokens();
  if (!tokens) return null;

  const remainingMs = tokens.expiresAt - Date.now();
  if (remainingMs > REFRESH_THRESHOLD_SEC * 1000) {
    return tokens.accessToken;
  }
  if (inFlightRefresh) return inFlightRefresh;

  inFlightRefresh = (async () => {
    try {
      const resp = await fetch(`${API_BASE}/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: tokens.refreshToken }),
      });
      if (!resp.ok) {
        clearTokens();
        return null;
      }
      const data: TokenResponse = await resp.json();
      storeTokens(tokensFromResponse(data));
      return data.access_token;
    } catch {
      clearTokens();
      return null;
    } finally {
      inFlightRefresh = null;
    }
  })();

  return inFlightRefresh;
}

export function getAccessToken(): string | null {
  return readTokens()?.accessToken ?? null;
}

export function getCurrentUser(): AuthUser | null {
  const tokens = readTokens();
  if (!tokens) return null;
  try {
    return userFromAccessToken(tokens.accessToken);
  } catch {
    return null;
  }
}

/**
 * Llamar al cargar la app. Si hay tokens en sessionStorage, intenta
 * dejarlos vigentes (refresh si están cerca de caducar) y devuelve al
 * usuario. Si no hay nada o ya no se puede renovar, devuelve null.
 */
export async function restoreSession(): Promise<AuthUser | null> {
  const tokens = readTokens();
  if (!tokens) return null;
  // Refresh defensivo: cubre el caso de F5 con un token recién caducado.
  const token = await refreshIfNeeded();
  if (!token) return null;
  return userFromAccessToken(token);
}
